import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Alert
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addDays, differenceInDays, startOfMonth, endOfMonth, isValid } from 'date-fns';

const ExportDialog = ({ open, onClose, onExport, currentTransactions }) => {
  const [exportType, setExportType] = useState('dateRange'); // 'dateRange' or 'month'
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [error, setError] = useState('');

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setExportType('dateRange');
      setStartDate(null);
      setEndDate(null);
      setSelectedMonth(null);
      setError('');
    }
  }, [open]);

  // Validate date range
  const validateDateRange = () => {
    if (exportType === 'dateRange') {
      if (!startDate || !endDate) {
        setError('Please select both start and end dates');
        return false;
      }

      if (!isValid(startDate) || !isValid(endDate)) {
        setError('Invalid date selection');
        return false;
      }

      const daysDiff = differenceInDays(endDate, startDate);
      if (daysDiff < 0) {
        setError('End date must be after start date');
        return false;
      }

      if (daysDiff > 15) {
        setError('Date range cannot exceed 15 days');
        return false;
      }
    } else {
      if (!selectedMonth) {
        setError('Please select a month');
        return false;
      }
    }

    setError('');
    return true;
  };

  const handleExport = () => {
    if (!validateDateRange()) return;

    let filteredTransactions;
    let fileName;

    if (exportType === 'dateRange') {
      // Format dates to match your transaction date format
      const formattedStartDate = startDate.toISOString().split('T')[0];
      const formattedEndDate = endDate.toISOString().split('T')[0];

      filteredTransactions = currentTransactions.filter(transaction => {
        return transaction.reservationDate >= formattedStartDate && 
               transaction.reservationDate <= formattedEndDate;
      });

      fileName = `transactions_${formattedStartDate}_to_${formattedEndDate}.csv`;
    } else {
      const monthStart = startOfMonth(selectedMonth);
      const monthEnd = endOfMonth(selectedMonth);
      const formattedMonthStart = monthStart.toISOString().split('T')[0];
      const formattedMonthEnd = monthEnd.toISOString().split('T')[0];

      filteredTransactions = currentTransactions.filter(transaction => {
        return transaction.reservationDate >= formattedMonthStart && 
               transaction.reservationDate <= formattedMonthEnd;
      });

      fileName = `transactions_${selectedMonth.toISOString().slice(0, 7)}.csv`;
    }

    // Create CSV content
    const headers = ['Booking ID', 'Court Name', 'Date', 'Time', 'Hours', 'Payment'];
    const csvContent = [
      headers.join(','),
      ...filteredTransactions.map(transaction => [
        transaction.bookingId,
        transaction.courtName,
        transaction.reservationDate,
        `${transaction.timeFrom} - ${transaction.timeTo}`,
        `${calculateTotalHours(transaction.timeFrom, transaction.timeTo)}`,
        `$${transaction.amountToPay}`
      ].join(','))
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    onClose();
  };

  function calculateTotalHours(timeFrom, timeTo) {
    const [fromHours, fromMinutes] = timeFrom.split(":").map(Number);
    const [toHours, toMinutes] = timeTo.split(":").map(Number);
    const from = new Date(0, 0, 0, fromHours, fromMinutes);
    const to = new Date(0, 0, 0, toHours, toMinutes);
    const diff = (to - from) / (1000 * 60 * 60);
    return diff.toFixed(2);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export Transactions</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Export Type</InputLabel>
            <Select
              value={exportType}
              label="Export Type"
              onChange={(e) => {
                setExportType(e.target.value);
                setError('');
              }}
            >
              <MenuItem value="dateRange">Date Range (max 15 days)</MenuItem>
              <MenuItem value="month">Whole Month</MenuItem>
            </Select>
          </FormControl>

          {exportType === 'dateRange' ? (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => {
                    setStartDate(newValue);
                    setError('');
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => {
                    setEndDate(newValue);
                    setError('');
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  minDate={startDate || undefined}
                  maxDate={startDate ? addDays(startDate, 15) : undefined}
                />
              </Box>
            </LocalizationProvider>
          ) : (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Select Month"
                views={['year', 'month']}
                value={selectedMonth}
                onChange={(newValue) => {
                  setSelectedMonth(newValue);
                  setError('');
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleExport} variant="contained" color="primary">
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDialog;